import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import './CryptoFlip.css';

// Contract ABIs - MATCHED TO YOUR ACTUAL CONTRACT
const CRYPTOFLIP_ABI = [
  // Game functions
  "function flip(uint8 choice, uint256 betAmount) external",
  "function getGameStats() external view returns (uint256 totalGames, uint256 totalBets, uint256 totalPayoutsAmount, uint256 houseProfit, uint256 companyTotal, uint256 routerTotal)",
  "function getRecentGames(uint256 count) external view returns (tuple(address player, uint256 betAmount, uint8 playerChoice, uint8 result, uint256 grossPayout, uint256 netPayout, uint256 timestamp, bool won)[])",
  "function getGameConfig() external view returns (tuple(uint256 noWinChance, uint256 headsWinChance, uint256 tailsWinChance, uint256 playerWinPayout))",
  "event GamePlayed(address indexed player, uint256 betAmount, uint8 playerChoice, uint8 result, uint256 grossPayout, uint256 netPayout, bool won)",
  
  // Admin functions - READ FUNCTIONS
  "function owner() external view returns (address)",
  "function admin() external view returns (address)",
  "function paused() external view returns (bool)",
  "function houseBalance() external view returns (uint256)",
  "function minBet() external view returns (uint256)",
  "function maxBet() external view returns (uint256)",
  
  // Admin functions - WRITE FUNCTIONS (onlyOwner or onlyOwnerOrAdmin)
  "function pauseGame() external",
  "function unpauseGame() external", 
  "function updateGameConfig(tuple(uint256 noWinChance, uint256 headsWinChance, uint256 tailsWinChance, uint256 playerWinPayout) newConfig) external",
  "function updateWinChances(uint256 noWin, uint256 headsWin, uint256 tailsWin) external",
  "function updatePayout(uint256 newPayout) external",
  "function setBetLimits(uint256 minBetAmount, uint256 maxBetAmount) external",
  "function fundHouse(uint256 amount) external", // onlyOwner
  "function withdrawHouse(uint256 amount) external", // onlyOwner
  "function emergencyWithdrawAll() external", // onlyOwner
  
  // Utility functions
  "function getEffectiveMultiplier() external view returns (uint256)",
  "function calculateHouseEdge() external view returns (uint256)"
];

const BLB_TOKEN_ABI = [
  "function balanceOf(address owner) external view returns (uint256)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function transfer(address to, uint256 amount) external returns (bool)"
];

// Contract Addresses
const CONTRACT_ADDRESSES = {
  CRYPTOFLIP: "0x4c1a98DC42faAF61F287Cb639e37f3a15BA69189",
  BLB_TOKEN: "0x9015e335f28cbad84EB255d5205CDb51B08A1B70"
};

// 🎵 EPIC SOUND SYSTEM 🎵
const playSound = (type, volume = 0.3) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const createTone = (frequency, duration, waveType = 'sine') => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.type = waveType;
      
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    };

    const createComplexSound = (notes, duration) => {
      notes.forEach((note, index) => {
        setTimeout(() => {
          createTone(note.freq, note.duration || duration, note.wave || 'sine');
        }, note.delay || index * 100);
      });
    };

    switch (type) {
      case 'coinFlip':
        createComplexSound([
          { freq: 200, duration: 0.1, wave: 'sawtooth', delay: 0 },
          { freq: 400, duration: 0.15, wave: 'triangle', delay: 50 },
          { freq: 800, duration: 0.2, wave: 'sine', delay: 100 },
          { freq: 1200, duration: 0.3, wave: 'sine', delay: 150 }
        ], 0.2);
        break;
        
      case 'victory':
        createComplexSound([
          { freq: 523, duration: 0.3, wave: 'triangle', delay: 0 },
          { freq: 659, duration: 0.3, wave: 'triangle', delay: 100 },
          { freq: 784, duration: 0.3, wave: 'triangle', delay: 200 },
          { freq: 1047, duration: 0.5, wave: 'sine', delay: 300 },
          { freq: 1319, duration: 0.6, wave: 'sine', delay: 400 }
        ], 0.3);
        break;
        
      case 'defeat':
        createComplexSound([
          { freq: 800, duration: 0.2, wave: 'triangle', delay: 0 },
          { freq: 600, duration: 0.2, wave: 'triangle', delay: 100 },
          { freq: 400, duration: 0.3, wave: 'sawtooth', delay: 200 },
          { freq: 200, duration: 0.4, wave: 'sawtooth', delay: 300 }
        ], 0.2);
        break;
        
      case 'buttonHover':
        createTone(800, 0.1, 'sine');
        break;
        
      case 'buttonClick':
        createComplexSound([
          { freq: 1000, duration: 0.05, wave: 'square', delay: 0 },
          { freq: 1200, duration: 0.05, wave: 'sine', delay: 20 }
        ], 0.05);
        break;
        
      case 'weaponSelect':
        createComplexSound([
          { freq: 300, duration: 0.1, wave: 'sawtooth', delay: 0 },
          { freq: 600, duration: 0.15, wave: 'triangle', delay: 50 },
          { freq: 900, duration: 0.2, wave: 'sine', delay: 100 }
        ], 0.15);
        break;
        
      case 'battleStart':
        createComplexSound([
          { freq: 200, duration: 0.2, wave: 'sawtooth', delay: 0 },
          { freq: 400, duration: 0.2, wave: 'triangle', delay: 100 },
          { freq: 800, duration: 0.3, wave: 'sine', delay: 200 },
          { freq: 1600, duration: 0.4, wave: 'sine', delay: 300 }
        ], 0.25);
        break;
        
      case 'admin':
        createComplexSound([
          { freq: 440, duration: 0.2, wave: 'sine', delay: 0 },
          { freq: 880, duration: 0.2, wave: 'sine', delay: 100 },
          { freq: 1760, duration: 0.3, wave: 'sine', delay: 200 }
        ], 0.2);
        break;
        
      default:
        createTone(440, 0.1);
    }
  } catch (error) {
    console.log('Audio not available:', error);
  }
};

const CryptoFlip = ({ walletData, onBack }) => {
  const [gameState, setGameState] = useState({
    betAmount: '1',
    selectedSide: null,
    isPlaying: false,
    isApproving: false,
    lastResult: null,
    balance: '0',
    allowance: '0',
    playerLevel: 1,
    experience: 0,
    streak: 0,
    showFlipPopup: false // NEW: For popup flip
  });

  const [gameStats, setGameStats] = useState({
    totalGames: 0,
    totalBets: '0',
    totalPayouts: '0',
    houseEdge: '5',
    effectiveMultiplier: '1.95',
    playersOnline: 0
  });

  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminData, setAdminData] = useState({
    contractBalance: '0',
    isPaused: false,
    minBet: '1',
    maxBet: '1000000',
    gameConfig: {
      noWinChance: 6000,
      headsWinChance: 2000,
      tailsWinChance: 2000,
      playerWinPayout: 195
    }
  });
  const [adminForm, setAdminForm] = useState({
    minBet: '1',
    maxBet: '1000000',
    fundAmount: '',
    withdrawAmount: '',
    noWinChance: '60',
    headsWinChance: '20',
    tailsWinChance: '20',
    playerWinPayout: '1.95'
  });
  const [adminLoading, setAdminLoading] = useState(false);

  const [gameHistory, setGameHistory] = useState([]);
  const [animationState, setAnimationState] = useState('idle');
  const [flipResult, setFlipResult] = useState(null);
  const [particles, setParticles] = useState([]);
  const [connected, setConnected] = useState(!!walletData?.account);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [contracts, setContracts] = useState({
    cryptoFlip: null,
    blbToken: null
  });
  const coinRef = useRef(null);

  const playGameSound = (type, volume = 0.3) => {
    if (soundEnabled) {
      playSound(type, volume);
    }
  };

  // Check if current wallet is admin - READS FROM CONTRACT
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (walletData?.account && contracts.cryptoFlip) {
        try {
          console.log('🔍 Checking admin status...');
          
          // Get owner and admin from contract
          const [owner, admin] = await Promise.all([
            contracts.cryptoFlip.owner().catch(() => null),
            contracts.cryptoFlip.admin().catch(() => null)
          ]);
          
          console.log('🔍 Contract Owner:', owner);
          console.log('🔍 Contract Admin:', admin);
          console.log('🔍 Current User:', walletData.account);
          
          const isOwner = owner && owner.toLowerCase() === walletData.account.toLowerCase();
          const isAdminRole = admin && admin.toLowerCase() === walletData.account.toLowerCase();
          const hasAdminAccess = isOwner || isAdminRole;
          
          console.log('🔍 Is Owner:', isOwner);
          console.log('🔍 Is Admin Role:', isAdminRole);
          console.log('🔍 Has Admin Access:', hasAdminAccess);
          
          setIsAdmin(hasAdminAccess);
          
          if (hasAdminAccess) {
            console.log('✅ Admin access granted');
            loadAdminData();
          } else {
            console.log('❌ No admin access');
            setIsAdmin(false);
            setShowAdminPanel(false);
          }
        } catch (error) {
          console.error('❌ Error checking admin status:', error);
          setIsAdmin(false);
          setShowAdminPanel(false);
        }
      } else {
        console.log('🔍 No wallet or contract available');
        setIsAdmin(false);
        setShowAdminPanel(false);
      }
    };

    // Only check admin status after contracts are loaded
    if (contracts.cryptoFlip) {
      checkAdminStatus();
    }
  }, [walletData?.account, contracts.cryptoFlip]);

  // 🔗 Initialize contracts directly
  useEffect(() => {
    if (walletData?.provider && walletData?.account) {
      initializeContracts();
    } else {
      setContracts({ cryptoFlip: null, blbToken: null });
    }
  }, [walletData?.provider, walletData?.account]);

  const initializeContracts = async () => {
    try {
      const signer = walletData.provider.getSigner();
      
      const cryptoFlipContract = new ethers.Contract(
        CONTRACT_ADDRESSES.CRYPTOFLIP,
        CRYPTOFLIP_ABI,
        signer
      );
      
      const blbTokenContract = new ethers.Contract(
        CONTRACT_ADDRESSES.BLB_TOKEN,
        BLB_TOKEN_ABI,
        signer
      );

      setContracts({
        cryptoFlip: cryptoFlipContract,
        blbToken: blbTokenContract
      });

      console.log('✅ Contracts initialized');

      // Load data after contracts are set
      setTimeout(() => {
        loadGameData(cryptoFlipContract);
        loadBalance(blbTokenContract, cryptoFlipContract);
      }, 500);

    } catch (error) {
      console.error('❌ Error initializing contracts:', error);
      setContracts({ cryptoFlip: null, blbToken: null });
    }
  };

  // 🛡️ Load admin data
  const loadAdminData = async (contract = contracts.cryptoFlip) => {
    try {
      if (!contract) return;

      console.log('🔄 Loading admin data...');

      const [houseBalance, isPaused, minBet, maxBet, config] = await Promise.all([
        contract.houseBalance().catch(err => {
          console.log('⚠️ Could not get house balance:', err.message);
          return ethers.BigNumber.from(0);
        }),
        contract.paused().catch(err => {
          console.log('⚠️ Could not get paused status:', err.message);
          return false;
        }),
        contract.minBet().catch(err => {
          console.log('⚠️ Could not get min bet:', err.message);
          return ethers.utils.parseEther('1');
        }),
        contract.maxBet().catch(err => {
          console.log('⚠️ Could not get max bet:', err.message);
          return ethers.utils.parseEther('1000000');
        }),
        contract.getGameConfig().catch(err => {
          console.log('⚠️ Could not get game config:', err.message);
          return {
            noWinChance: 6000,
            headsWinChance: 2000,
            tailsWinChance: 2000,
            playerWinPayout: 195
          };
        })
      ]);

      const adminInfo = {
        contractBalance: ethers.utils.formatEther(houseBalance),
        isPaused,
        minBet: ethers.utils.formatEther(minBet),
        maxBet: ethers.utils.formatEther(maxBet),
        gameConfig: {
          noWinChance: parseInt(config.noWinChance),
          headsWinChance: parseInt(config.headsWinChance),
          tailsWinChance: parseInt(config.tailsWinChance),
          playerWinPayout: parseInt(config.playerWinPayout)
        }
      };

      console.log('✅ Admin data loaded:', adminInfo);

      setAdminData(adminInfo);
      setAdminForm({
        minBet: adminInfo.minBet,
        maxBet: adminInfo.maxBet,
        fundAmount: '',
        withdrawAmount: '',
        noWinChance: (adminInfo.gameConfig.noWinChance / 100).toString(),
        headsWinChance: (adminInfo.gameConfig.headsWinChance / 100).toString(),
        tailsWinChance: (adminInfo.gameConfig.tailsWinChance / 100).toString(),
        playerWinPayout: (adminInfo.gameConfig.playerWinPayout / 100).toString()
      });

    } catch (error) {
      console.error('❌ Error loading admin data:', error);
    }
  };

  // 🎮 Particle effects
  useEffect(() => {
    if (animationState === 'result' && flipResult?.won) {
      const newParticles = Array.from({ length: 20 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 4 + 2,
        life: 100,
        velocity: {
          x: (Math.random() - 0.5) * 3,
          y: Math.random() * -2 - 1
        }
      }));
      setParticles(newParticles);
      
      const interval = setInterval(() => {
        setParticles(prev => prev.map(p => ({ 
          ...p, 
          life: p.life - 3,
          x: p.x + p.velocity.x,
          y: p.y + p.velocity.y
        })).filter(p => p.life > 0));
      }, 50);
      
      setTimeout(() => clearInterval(interval), 3000);
    }
  }, [animationState, flipResult]);

  // 🔗 Connection state
  useEffect(() => {
    if (walletData?.account) {
      setConnected(true);
    }
  }, [walletData]);

  // 📊 Load game data from contract
  const loadGameData = async (contract = contracts.cryptoFlip) => {
    try {
      if (!contract) {
        setGameStats({
          totalGames: 0,
          totalBets: '0',
          totalPayouts: '0',
          houseEdge: '60',
          effectiveMultiplier: '1.755',
          playersOnline: Math.floor(Math.random() * 300) + 50
        });
        return;
      }

      // Try individual calls with fallbacks
      let stats, recentGames, effectiveMultiplier, houseEdge;

      try {
        stats = await contract.getGameStats();
      } catch (e) {
        stats = {
          totalGames: ethers.BigNumber.from(0),
          totalBets: ethers.BigNumber.from(0),
          totalPayoutsAmount: ethers.BigNumber.from(0)
        };
      }

      try {
        recentGames = await contract.getRecentGames(5);
      } catch (e) {
        recentGames = [];
      }

      try {
        effectiveMultiplier = await contract.getEffectiveMultiplier();
      } catch (e) {
        effectiveMultiplier = ethers.BigNumber.from(175); // 1.75x default
      }

      try {
        houseEdge = await contract.calculateHouseEdge();
      } catch (e) {
        houseEdge = ethers.BigNumber.from(6000); // 60% default
      }

      setGameStats({
        totalGames: parseInt(stats.totalGames.toString()),
        totalBets: ethers.utils.formatEther(stats.totalBets),
        totalPayouts: ethers.utils.formatEther(stats.totalPayoutsAmount),
        houseEdge: (parseInt(houseEdge.toString()) / 100).toString(),
        effectiveMultiplier: (parseInt(effectiveMultiplier.toString()) / 100).toString(),
        playersOnline: Math.floor(Math.random() * 300) + 50
      });

      if (recentGames && recentGames.length > 0) {
        const formattedHistory = recentGames.slice(0, 5).map(game => ({
          playerChoice: parseInt(game.playerChoice),
          won: game.won,
          betAmount: ethers.utils.formatEther(game.betAmount),
          netPayout: ethers.utils.formatEther(game.netPayout),
          timestamp: parseInt(game.timestamp) * 1000
        }));
        setGameHistory(formattedHistory);
      }

    } catch (error) {
      console.log('Using default game data');
      setGameStats({
        totalGames: 0,
        totalBets: '0',
        totalPayouts: '0',
        houseEdge: '60',
        effectiveMultiplier: '1.755',
        playersOnline: Math.floor(Math.random() * 300) + 50
      });
    }
  };

  // 💰 Load BLB balance
  const loadBalance = async (tokenContract = contracts.blbToken, gameContract = contracts.cryptoFlip) => {
    try {
      if (!tokenContract || !gameContract || !walletData?.account) {
        setGameState(prev => ({
          ...prev,
          balance: '1000', // Demo balance
          allowance: '0'
        }));
        return;
      }

      let balance, allowance;

      try {
        balance = await tokenContract.balanceOf(walletData.account);
      } catch (e) {
        balance = ethers.utils.parseEther('1000'); // Demo balance
      }

      try {
        allowance = await tokenContract.allowance(walletData.account, gameContract.address);
      } catch (e) {
        allowance = ethers.BigNumber.from(0);
      }

      setGameState(prev => ({
        ...prev,
        balance: ethers.utils.formatEther(balance),
        allowance: ethers.utils.formatEther(allowance)
      }));

    } catch (error) {
      console.log('Using demo balance');
      setGameState(prev => ({
        ...prev,
        balance: '1000',
        allowance: '0'
      }));
    }
  };

  // 🛡️ Admin Functions - FIXED TO MATCH CONTRACT
  const handlePauseGame = async () => {
    try {
      setAdminLoading(true);
      setErrorMessage('');
      playGameSound('admin', 0.3);

      if (contracts.cryptoFlip) {
        console.log('🔄 Pausing game...');
        const tx = await contracts.cryptoFlip.pauseGame();
        console.log('📤 Transaction sent:', tx.hash);
        await tx.wait();
        console.log('✅ Game paused');
        setSuccessMessage('✅ Game paused successfully!');
      } else {
        setSuccessMessage('✅ Game paused! (Demo)');
      }

      await loadAdminData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('❌ Pause error:', error);
      setErrorMessage('Failed to pause game: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUnpauseGame = async () => {
    try {
      setAdminLoading(true);
      setErrorMessage('');
      playGameSound('admin', 0.3);

      if (contracts.cryptoFlip) {
        console.log('🔄 Unpausing game...');
        const tx = await contracts.cryptoFlip.unpauseGame();
        console.log('📤 Transaction sent:', tx.hash);
        await tx.wait();
        console.log('✅ Game unpaused');
        setSuccessMessage('✅ Game unpaused successfully!');
      } else {
        setSuccessMessage('✅ Game unpaused! (Demo)');
      }

      await loadAdminData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('❌ Unpause error:', error);
      setErrorMessage('Failed to unpause game: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleEmergencyWithdraw = async () => {
    try {
      setAdminLoading(true);
      setErrorMessage('');
      playGameSound('admin', 0.3);

      if (contracts.cryptoFlip) {
        console.log('🔄 Emergency withdrawal...');
        const tx = await contracts.cryptoFlip.emergencyWithdrawAll();
        console.log('📤 Transaction sent:', tx.hash);
        await tx.wait();
        console.log('✅ Emergency withdrawal completed');
        setSuccessMessage('✅ Emergency withdrawal completed!');
      } else {
        setSuccessMessage('✅ Emergency withdrawal completed! (Demo)');
      }

      await Promise.all([loadAdminData(), loadBalance()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('❌ Emergency withdraw error:', error);
      setErrorMessage('Failed to emergency withdraw: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleFundHouse = async () => {
    try {
      if (!adminForm.fundAmount || parseFloat(adminForm.fundAmount) <= 0) {
        setErrorMessage('Please enter a valid fund amount');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');
      playGameSound('admin', 0.3);

      const fundAmountWei = ethers.utils.parseEther(adminForm.fundAmount);

      if (contracts.cryptoFlip && contracts.blbToken) {
        console.log('🔄 Funding house...');
        
        // First approve if needed
        const allowance = await contracts.blbToken.allowance(walletData.account, contracts.cryptoFlip.address);
        if (allowance.lt(fundAmountWei)) {
          console.log('🔄 Approving tokens...');
          const approveTx = await contracts.blbToken.approve(contracts.cryptoFlip.address, ethers.constants.MaxUint256);
          await approveTx.wait();
          console.log('✅ Tokens approved');
        }

        const tx = await contracts.cryptoFlip.fundHouse(fundAmountWei);
        console.log('📤 Transaction sent:', tx.hash);
        await tx.wait();
        console.log('✅ House funded');
        setSuccessMessage(`✅ House funded with ${adminForm.fundAmount} BLB!`);
      } else {
        setSuccessMessage(`✅ House funded with ${adminForm.fundAmount} BLB! (Demo)`);
      }

      setAdminForm(prev => ({ ...prev, fundAmount: '' }));
      await Promise.all([loadAdminData(), loadBalance()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('❌ Fund house error:', error);
      setErrorMessage('Failed to fund house: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleWithdrawHouse = async () => {
    try {
      if (!adminForm.withdrawAmount || parseFloat(adminForm.withdrawAmount) <= 0) {
        setErrorMessage('Please enter a valid withdraw amount');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');
      playGameSound('admin', 0.3);

      const withdrawAmountWei = ethers.utils.parseEther(adminForm.withdrawAmount);

      if (contracts.cryptoFlip) {
        console.log('🔄 Withdrawing from house...');
        const tx = await contracts.cryptoFlip.withdrawHouse(withdrawAmountWei);
        console.log('📤 Transaction sent:', tx.hash);
        await tx.wait();
        console.log('✅ House withdrawal completed');
        setSuccessMessage(`✅ Withdrew ${adminForm.withdrawAmount} BLB from house!`);
      } else {
        setSuccessMessage(`✅ Withdrew ${adminForm.withdrawAmount} BLB from house! (Demo)`);
      }

      setAdminForm(prev => ({ ...prev, withdrawAmount: '' }));
      await Promise.all([loadAdminData(), loadBalance()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('❌ Withdraw house error:', error);
      setErrorMessage('Failed to withdraw from house: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleSetBetLimits = async () => {
    try {
      const minBet = parseFloat(adminForm.minBet);
      const maxBet = parseFloat(adminForm.maxBet);

      if (minBet <= 0 || maxBet <= 0 || minBet >= maxBet) {
        setErrorMessage('Invalid bet limits');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');
      playGameSound('admin', 0.3);

      if (contracts.cryptoFlip) {
        console.log('🔄 Setting bet limits...');
        const minBetWei = ethers.utils.parseEther(adminForm.minBet);
        const maxBetWei = ethers.utils.parseEther(adminForm.maxBet);
        
        const tx = await contracts.cryptoFlip.setBetLimits(minBetWei, maxBetWei);
        console.log('📤 Transaction sent:', tx.hash);
        await tx.wait();
        console.log('✅ Bet limits updated');
        setSuccessMessage(`✅ Bet limits updated! Min: ${adminForm.minBet} BLB, Max: ${adminForm.maxBet} BLB`);
      } else {
        setSuccessMessage(`✅ Bet limits updated! Min: ${adminForm.minBet} BLB, Max: ${adminForm.maxBet} BLB (Demo)`);
      }

      await loadAdminData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('❌ Set bet limits error:', error);
      setErrorMessage('Failed to set bet limits: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUpdateWinChances = async () => {
    try {
      const noWin = Math.round(parseFloat(adminForm.noWinChance) * 100);
      const heads = Math.round(parseFloat(adminForm.headsWinChance) * 100);
      const tails = Math.round(parseFloat(adminForm.tailsWinChance) * 100);

      console.log('🔍 Win chances values:', { noWin, heads, tails, total: noWin + heads + tails });

      if (noWin + heads + tails !== 10000) {
        setErrorMessage('Total chances must equal 100%');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');
      playGameSound('admin', 0.3);

      if (contracts.cryptoFlip) {
        try {
          console.log('🔄 Updating win chances...');
          
          const tx = await contracts.cryptoFlip.updateWinChances(noWin, heads, tails);
          console.log('📤 Transaction sent:', tx.hash);
          
          const receipt = await tx.wait();
          console.log('✅ Transaction confirmed:', receipt);
          
          setSuccessMessage('✅ Win chances updated successfully!');
        } catch (contractError) {
          console.error('❌ Contract error details:', contractError);
          
          // Provide more specific error messages
          if (contractError.message.includes('Only owner or admin')) {
            setErrorMessage('❌ Access denied: Only contract owner or admin can update win chances');
          } else if (contractError.message.includes('execution reverted')) {
            setErrorMessage('❌ Transaction reverted: ' + (contractError.reason || 'Check permissions and values'));
          } else if (contractError.message.includes('insufficient funds')) {
            setErrorMessage('❌ Insufficient funds for transaction');
          } else {
            setErrorMessage('❌ Contract call failed: ' + (contractError.reason || contractError.message));
          }
          return;
        }
      } else {
        setSuccessMessage('✅ Win chances updated successfully! (Demo)');
      }

      await Promise.all([loadAdminData(), loadGameData()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('❌ Update win chances error:', error);
      setErrorMessage('Failed to update win chances: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUpdatePayout = async () => {
    try {
      const payout = Math.round(parseFloat(adminForm.playerWinPayout) * 100);

      if (payout < 100 || payout > 300) {
        setErrorMessage('Payout must be between 1x-3x');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');
      playGameSound('admin', 0.3);

      if (contracts.cryptoFlip) {
        console.log('🔄 Updating payout...');
        const tx = await contracts.cryptoFlip.updatePayout(payout);
        console.log('📤 Transaction sent:', tx.hash);
        await tx.wait();
        console.log('✅ Payout updated');
        setSuccessMessage('✅ Payout updated successfully!');
      } else {
        setSuccessMessage('✅ Payout updated successfully! (Demo)');
      }

      await Promise.all([loadAdminData(), loadGameData()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('❌ Update payout error:', error);
      setErrorMessage('Failed to update payout: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  // 🎯 Handle coin flip - UPDATED WITH POPUP
  const handleFlip = async () => {
  if (gameState.selectedSide === null) return;

  try {
    setErrorMessage('');
    setSuccessMessage('');

    // Validate bet amount
    const betAmount = parseFloat(gameState.betAmount);
    const balance = parseFloat(gameState.balance);

    if (betAmount <= 0 || betAmount > balance) {
      setErrorMessage('Invalid bet amount');
      return;
    }

    playGameSound('battleStart', 0.4);
    
    // Show popup and start flipping
    setGameState(prev => ({ 
      ...prev, 
      isPlaying: true, 
      showFlipPopup: true 
    }));
    setAnimationState('flipping');

    setTimeout(() => playGameSound('coinFlip', 0.5), 500);

    // If contracts available, use real blockchain
    if (contracts.cryptoFlip && contracts.blbToken) {
      try {
        const betAmountWei = ethers.utils.parseEther(gameState.betAmount);
        const tx = await contracts.cryptoFlip.flip(gameState.selectedSide, betAmountWei);
        
        setSuccessMessage('Transaction submitted! Flipping coin...');
        
        const receipt = await tx.wait();
        
        const gamePlayedEvent = receipt.events?.find(event => event.event === 'GamePlayed');
        
        if (gamePlayedEvent) {
          const [player, betAmount, playerChoice, result, grossPayout, netPayout, won] = gamePlayedEvent.args;
          
          const gameResult = {
            player,
            betAmount: ethers.utils.formatEther(betAmount),
            playerChoice: parseInt(playerChoice),
            result: parseInt(result),
            grossPayout: ethers.utils.formatEther(grossPayout),
            netPayout: ethers.utils.formatEther(netPayout),
            won,
            timestamp: Date.now()
          };

          setTimeout(() => {
            handleGameResult(gameResult, true);
          }, 2500);
          
          await Promise.all([loadGameData(), loadBalance()]);
          return;
        }
      } catch (contractError) {
        console.error('Contract transaction failed:', contractError);
        
        // Show specific error message and reset state
        setErrorMessage('Wallet connection low bandwidth. Refresh and try again');
        setAnimationState('idle');
        setGameState(prev => ({ 
          ...prev, 
          isPlaying: false, 
          showFlipPopup: false 
        }));
        return;
      }
    }

    // If no contracts available, show connection error
    setErrorMessage('Smart contract not connected. Please refresh the page and connect your wallet.');
    setAnimationState('idle');
    setGameState(prev => ({ 
      ...prev, 
      isPlaying: false, 
      showFlipPopup: false 
    }));

  } catch (error) {
    console.error('Game error:', error);
    setErrorMessage('Transaction failed. Please refresh and try again.');
    setAnimationState('idle');
    setGameState(prev => ({ 
      ...prev, 
      isPlaying: false, 
      showFlipPopup: false 
    }));
  }
};

  const handleGameResult = (gameResult, isReal) => {
    setFlipResult(gameResult);
    setAnimationState('result');

    setTimeout(() => {
      if (gameResult.won) {
        playGameSound('victory', 0.6);
        setSuccessMessage(`🎉 You won ${formatNumber(gameResult.netPayout)} BLB!`);
      } else {
        playGameSound('defeat', 0.4);
        setErrorMessage(`💀 You lost ${formatNumber(gameResult.betAmount)} BLB`);
      }
    }, 300);

    // Update local state for simulation
    if (!isReal) {
      setGameState(prev => ({
        ...prev,
        isPlaying: false,
        balance: gameResult.won 
          ? (parseFloat(prev.balance) + parseFloat(gameResult.netPayout) - parseFloat(gameResult.betAmount)).toString()
          : (parseFloat(prev.balance) - parseFloat(gameResult.betAmount)).toString(),
        streak: gameResult.won ? prev.streak + 1 : 0,
        experience: prev.experience + (gameResult.won ? 50 : 10),
        playerLevel: Math.floor((prev.experience + (gameResult.won ? 50 : 10)) / 100) + 1
      }));

      setGameHistory(prev => [gameResult, ...prev.slice(0, 4)]);
    } else {
      setGameState(prev => ({ ...prev, isPlaying: false }));
    }

    // Reset after showing result
    setTimeout(() => {
      setAnimationState('idle');
      setFlipResult(null);
      setGameState(prev => ({ 
        ...prev, 
        selectedSide: null,
        isPlaying: false,
        showFlipPopup: false // Close popup
      }));
      setErrorMessage('');
      setSuccessMessage('');
    }, 4000);
  };

  // 🔓 Handle token approval
  const handleApprove = async () => {
    try {
      setErrorMessage('');
      playGameSound('buttonClick', 0.3);
      setGameState(prev => ({ ...prev, isApproving: true }));

      if (contracts.blbToken && contracts.cryptoFlip) {
        const maxAmount = ethers.constants.MaxUint256;
        const tx = await contracts.blbToken.approve(contracts.cryptoFlip.address, maxAmount);
        await tx.wait();
        await loadBalance();
        setSuccessMessage('✅ Approval successful!');
      } else {
        // Simulate approval
        setGameState(prev => ({ ...prev, allowance: '999999999' }));
        setSuccessMessage('✅ Approval successful!');
      }

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Approval error:', error);
      setErrorMessage('Approval failed: ' + error.message);
    } finally {
      setGameState(prev => ({ ...prev, isApproving: false }));
    }
  };

  const handleWeaponSelect = (side) => {
    playGameSound('weaponSelect', 0.3);
    setGameState(prev => ({ ...prev, selectedSide: side }));
  };

  const needsApproval = () => {
    return parseFloat(gameState.allowance) < parseFloat(gameState.betAmount);
  };

  const canPlay = () => {
    return connected && 
           gameState.selectedSide !== null && 
           parseFloat(gameState.balance) >= parseFloat(gameState.betAmount) &&
           !needsApproval() &&
           !gameState.isPlaying &&
           !adminData.isPaused;
  };

  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toFixed(0);
  };

  if (!connected) {
    return (
      <div className="cryptoflip-battle-arena">
        <div className="cryptoflip-connect-screen">
          <div className="cryptoflip-connect-icon">⚡</div>
          <h3 className="cryptoflip-connect-title">🚀 ENTER THE ARENA</h3>
          <p className="cryptoflip-connect-desc">Connect your wallet to access the CryptoFlip Battle Arena</p>
          <button 
            className="cryptoflip-action-btn cryptoflip-flip-btn"
            onClick={() => {
              playGameSound('buttonClick', 0.4);
              setConnected(true);
              setGameState(prev => ({ ...prev, balance: '1000' })); // Demo balance
            }}
            onMouseEnter={() => playGameSound('buttonHover', 0.2)}
            style={{ marginTop: '2rem', width: 'auto', padding: '1rem 2rem' }}
          >
            🔗 CONNECT WALLET
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cryptoflip-battle-arena">
      {/* Epic Battle Header */}
      <div className="cryptoflip-header-bar">
        <button 
          className="cryptoflip-back-btn" 
          onClick={() => {
            playGameSound('buttonClick', 0.3);
            if (onBack) onBack();
          }}
          onMouseEnter={() => playGameSound('buttonHover', 0.2)}
        >
          ← Retreat
        </button>
        <div className="cryptoflip-title-section">
          <h2 className="cryptoflip-main-title">🏆 CRYPTOFLIP</h2>
          <div className="cryptoflip-subtitle">{gameStats.effectiveMultiplier}x Multiplier</div>
          {adminData.isPaused && (
            <div style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: '800', marginTop: '0.2rem' }}>
              ⚠️ GAME PAUSED
            </div>
          )}
        </div>
        <div className="cryptoflip-balance-display">
          💎 {formatNumber(gameState.balance)} BLB
          <div className="cryptoflip-player-level">
            LVL {gameState.playerLevel}
          </div>
          {isAdmin && (
            <button
              onClick={() => {
                setShowAdminPanel(!showAdminPanel);
                playGameSound('admin', 0.3);
              }}
              style={{
                position: 'absolute',
                top: '5px',
                left: '5px',
                background: 'linear-gradient(45deg, #dc2626, #ffd700)',
                border: '1px solid #dc2626',
                borderRadius: '6px',
                padding: '0.3rem',
                color: '#000',
                fontWeight: '800',
                fontSize: '0.8rem',
                cursor: 'pointer',
                width: '2.5rem',
                height: '2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 8px rgba(255, 215, 0, 0.4)'
              }}
              title="Admin Panel"
            >
              🛡️
            </button>
          )}
          <button
            onClick={() => {
              setSoundEnabled(!soundEnabled);
              playGameSound('buttonClick', 0.2);
            }}
            className="cryptoflip-sound-btn"
            title={soundEnabled ? 'Disable Sound' : 'Enable Sound'}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
        </div>
      </div>

      {/* Admin Panel */}
      {isAdmin && showAdminPanel && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(13, 16, 23, 0.98) 0%, rgba(17, 24, 39, 0.95) 100%)',
            border: '3px solid #dc2626',
            borderRadius: '20px',
            padding: '2rem',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h2 style={{ color: '#dc2626', fontSize: '1.8rem', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '2px', margin: 0 }}>
                🛡️ ADMIN PANEL
              </h2>
              <button
                onClick={() => setShowAdminPanel(false)}
                style={{
                  background: '#ef4444',
                  border: 'none',
                  color: 'white',
                  width: '2.5rem',
                  height: '2.5rem',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  fontWeight: '800'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
              {/* Game Status */}
              <div className="cryptoflip-control-panel">
                <label className="cryptoflip-control-label">🎮 GAME STATUS</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>
                    Status: <span style={{ color: adminData.isPaused ? '#ef4444' : '#10b981' }}>
                      {adminData.isPaused ? '⚠️ PAUSED' : '✅ ACTIVE'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      className="cryptoflip-action-btn"
                      onClick={handlePauseGame}
                      disabled={adminLoading || adminData.isPaused}
                      style={{
                        flex: 1,
                        padding: '1rem',
                        fontSize: '0.9rem',
                        background: adminData.isPaused ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
                        borderColor: '#ef4444'
                      }}
                    >
                      ⏸️ PAUSE
                    </button>
                    <button
                      className="cryptoflip-action-btn"
                      onClick={handleUnpauseGame}
                      disabled={adminLoading || !adminData.isPaused}
                      style={{
                        flex: 1,
                        padding: '1rem',
                        fontSize: '0.9rem',
                        background: !adminData.isPaused ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #10b981, #059669)',
                        borderColor: '#10b981'
                      }}
                    >
                      ▶️ UNPAUSE
                    </button>
                  </div>
                </div>
              </div>

              {/* House Balance */}
              <div className="cryptoflip-control-panel">
                <label className="cryptoflip-control-label">🏦 HOUSE BALANCE</label>
                <div style={{ color: '#10b981', fontSize: '1.5rem', fontWeight: '800', marginBottom: '1rem' }}>
                  💎 {formatNumber(adminData.contractBalance)} BLB
                </div>
                <button
                  className="cryptoflip-action-btn"
                  onClick={handleEmergencyWithdraw}
                  disabled={adminLoading}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    fontSize: '0.9rem',
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    borderColor: '#f59e0b'
                  }}
                >
                  {adminLoading ? '⏳ PROCESSING...' : '🚨 EMERGENCY WITHDRAW ALL'}
                </button>
              </div>

              {/* Fund House */}
              <div className="cryptoflip-control-panel">
                <label className="cryptoflip-control-label">💰 FUND HOUSE</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    className="cryptoflip-stake-input"
                    type="number"
                    placeholder="Amount to fund"
                    value={adminForm.fundAmount}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, fundAmount: e.target.value }))}
                    min="1"
                    step="1"
                    style={{ marginBottom: 0 }}
                  />
                  <button
                    className="cryptoflip-action-btn"
                    onClick={handleFundHouse}
                    disabled={adminLoading || !adminForm.fundAmount}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '0.9rem',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      borderColor: '#10b981'
                    }}
                  >
                    {adminLoading ? '⏳ FUNDING...' : '💎 FUND HOUSE'}
                  </button>
                </div>
              </div>

              {/* Withdraw House */}
              <div className="cryptoflip-control-panel">
                <label className="cryptoflip-control-label">💸 WITHDRAW HOUSE</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    className="cryptoflip-stake-input"
                    type="number"
                    placeholder="Amount to withdraw"
                    value={adminForm.withdrawAmount}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, withdrawAmount: e.target.value }))}
                    min="1"
                    step="1"
                    style={{ marginBottom: 0 }}
                  />
                  <button
                    className="cryptoflip-action-btn"
                    onClick={handleWithdrawHouse}
                    disabled={adminLoading || !adminForm.withdrawAmount}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '0.9rem',
                      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                      borderColor: '#f59e0b'
                    }}
                  >
                    {adminLoading ? '⏳ WITHDRAWING...' : '💸 WITHDRAW'}
                  </button>
                </div>
              </div>

              {/* Bet Limits */}
              <div className="cryptoflip-control-panel">
                <label className="cryptoflip-control-label">🎯 BET LIMITS</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>MIN BET</label>
                      <input
                        className="cryptoflip-stake-input"
                        type="number"
                        value={adminForm.minBet}
                        onChange={(e) => setAdminForm(prev => ({ ...prev, minBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{ marginBottom: 0 }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>MAX BET</label>
                      <input
                        className="cryptoflip-stake-input"
                        type="number"
                        value={adminForm.maxBet}
                        onChange={(e) => setAdminForm(prev => ({ ...prev, maxBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{ marginBottom: 0 }}
                      />
                    </div>
                  </div>
                  <button
                    className="cryptoflip-action-btn"
                    onClick={handleSetBetLimits}
                    disabled={adminLoading}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '0.9rem',
                      background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                      borderColor: '#3b82f6'
                    }}
                  >
                    {adminLoading ? '⏳ UPDATING...' : '🎯 SET LIMITS'}
                  </button>
                </div>
              </div>

              {/* Win Chances */}
              <div className="cryptoflip-control-panel" style={{ gridColumn: '1 / -1' }}>
                <label className="cryptoflip-control-label">🎰 WIN CHANCES</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      HOUSE WIN (%)
                    </label>
                    <input
                      className="cryptoflip-stake-input"
                      type="number"
                      value={adminForm.noWinChance}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, noWinChance: e.target.value }))}
                      min="0"
                      max="100"
                      step="0.1"
                      style={{ marginBottom: 0 }}
                      placeholder="60.0"
                    />
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      HEADS WIN (%)
                    </label>
                    <input
                      className="cryptoflip-stake-input"
                      type="number"
                      value={adminForm.headsWinChance}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, headsWinChance: e.target.value }))}
                      min="0"
                      max="100"
                      step="0.1"
                      style={{ marginBottom: 0 }}
                      placeholder="20.0"
                    />
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      TAILS WIN (%)
                    </label>
                    <input
                      className="cryptoflip-stake-input"
                      type="number"
                      value={adminForm.tailsWinChance}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, tailsWinChance: e.target.value }))}
                      min="0"
                      max="100"
                      step="0.1"
                      style={{ marginBottom: 0 }}
                      placeholder="20.0"
                    />
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      PAYOUT (x)
                    </label>
                    <input
                      className="cryptoflip-stake-input"
                      type="number"
                      value={adminForm.playerWinPayout}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, playerWinPayout: e.target.value }))}
                      min="1"
                      max="3"
                      step="0.01"
                      style={{ marginBottom: 0 }}
                      placeholder="1.95"
                    />
                  </div>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  color: 'rgba(255,255,255,0.7)', 
                  fontSize: '0.9rem', 
                  marginBottom: '1rem',
                  padding: '1rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div>
                    <strong>Total Percentage:</strong> {((parseFloat(adminForm.noWinChance || 0) + parseFloat(adminForm.headsWinChance || 0) + parseFloat(adminForm.tailsWinChance || 0)).toFixed(1))}%
                  </div>
                  <div>
                    <strong>House Edge:</strong> <span style={{ color: '#ffd700', fontWeight: '800' }}>{parseFloat(adminForm.noWinChance || 0).toFixed(1)}%</span>
                  </div>
                  {Math.abs((parseFloat(adminForm.noWinChance || 0) + parseFloat(adminForm.headsWinChance || 0) + parseFloat(adminForm.tailsWinChance || 0)) - 100) > 0.1 && (
                    <span style={{ color: '#ef4444', fontWeight: '800' }}>⚠️ Must equal 100%</span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    className="cryptoflip-action-btn"
                    onClick={handleUpdateWinChances}
                    disabled={adminLoading || Math.abs((parseFloat(adminForm.noWinChance || 0) + parseFloat(adminForm.headsWinChance || 0) + parseFloat(adminForm.tailsWinChance || 0)) - 100) > 0.1}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      fontSize: '0.9rem',
                      background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                      borderColor: '#8b5cf6'
                    }}
                  >
                    {adminLoading ? '⏳ UPDATING...' : '🎰 UPDATE CHANCES'}
                  </button>
                  <button
                    className="cryptoflip-action-btn"
                    onClick={handleUpdatePayout}
                    disabled={adminLoading}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      fontSize: '0.9rem',
                      background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
                      borderColor: '#06b6d4'
                    }}
                  >
                    {adminLoading ? '⏳ UPDATING...' : '💰 UPDATE PAYOUT'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error/Success Messages */}
      {errorMessage && (
        <div className="cryptoflip-message cryptoflip-error-msg">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="cryptoflip-message cryptoflip-success-msg">
          {successMessage}
        </div>
      )}

      <div className="cryptoflip-game-layout">
        {/* Main Game Zone */}
        <div className="cryptoflip-game-zone">
          <div className="cryptoflip-coin-arena">
            <div 
              ref={coinRef}
              className={`cryptoflip-coin ${
                animationState === 'flipping' ? 'flip-animation' :
                animationState === 'result' && flipResult?.won ? 'victory-animation' :
                animationState === 'result' && !flipResult?.won ? 'defeat-animation' : ''
              }`}
              onClick={!gameState.isPlaying ? () => {
                if (animationState === 'idle') {
                  playGameSound('buttonClick', 0.2);
                  const coin = coinRef.current;
                  coin.style.transform = 'scale(1.1) rotateY(180deg)';
                  setTimeout(() => {
                    coin.style.transform = 'scale(1) rotateY(0deg)';
                  }, 300);
                }
              } : undefined}
            >
              <div className="cryptoflip-coin-inner">
                <div className="cryptoflip-coin-face">
                  {animationState === 'result' && flipResult ? 
                    (flipResult.result === 0 ? '👑' : flipResult.result === 1 ? '⭐' : '🎭') : '🪙'
                  }
                </div>
                <div className="cryptoflip-coin-face cryptoflip-coin-back">
                  {animationState === 'result' && flipResult ? 
                    (flipResult.result === 0 ? '👑' : flipResult.result === 1 ? '⭐' : '🎭') : '🪙'
                  }
                </div>
              </div>
            </div>

            {/* Victory Particles */}
            {particles.map(particle => (
              <div
                key={particle.id}
                style={{
                  position: 'absolute',
                  left: `${particle.x}%`,
                  top: `${particle.y}%`,
                  width: `${particle.size}px`,
                  height: `${particle.size}px`,
                  background: `radial-gradient(circle, 
                    hsl(${Math.random() * 60 + 40}, 100%, 70%), 
                    hsl(${Math.random() * 60 + 40}, 100%, 50%))`,
                  borderRadius: '50%',
                  opacity: particle.life / 100,
                  animation: 'particleFloat 3s ease-out',
                  pointerEvents: 'none',
                  zIndex: 5,
                  boxShadow: '0 0 10px currentColor'
                }}
              />
            ))}
          </div>

          {/* EPIC FLIP POPUP - USING CSS CLASSES */}
{gameState.showFlipPopup && (
  <div className="cryptoflip-flip-popup-overlay">
    <div className="cryptoflip-flip-popup">
      <div className="cryptoflip-popup-header">
        <h2>🎰 FLIPPING COIN OF FATE!</h2>
        <div className="cryptoflip-bet-info">
          <span>Stake: {formatNumber(gameState.betAmount)} BLB</span>
          <span>Choice: {gameState.selectedSide === 0 ? '👑 HEADS' : '⭐ TAILS'}</span>
        </div>
      </div>

      <div className="cryptoflip-popup-coin-container">
        <div 
          className={`cryptoflip-popup-coin ${
            animationState === 'flipping' ? 'flipping-animation' :
            animationState === 'result' && flipResult?.won ? 'victory-animation' :
            animationState === 'result' && !flipResult?.won ? 'defeat-animation' : ''
          }`}
        >
          {animationState === 'result' && flipResult ? 
            (flipResult.result === 0 ? '👑' : flipResult.result === 1 ? '⭐' : '🎭') : '🪙'
          }
        </div>
      </div>

      {/* Result Display in Popup */}
      {animationState === 'result' && flipResult && (
        <div className="cryptoflip-popup-result-display">
          <div className={`cryptoflip-popup-result-content ${flipResult.won ? 'cryptoflip-popup-victory' : 'cryptoflip-popup-defeat'}`}>
            <div className="cryptoflip-popup-result-icon">
              {flipResult.won ? '🎊' : '💥'}
            </div>
            <h3 className="cryptoflip-popup-result-title">
              {flipResult.won ? '🏆 EPIC VICTORY!' : '⚔️ VALIANT EFFORT'}
            </h3>
            <div className="cryptoflip-popup-result-amount">
              {flipResult.won ? `+${formatNumber(flipResult.netPayout)}` : `-${formatNumber(flipResult.betAmount)}`} BLB
            </div>
            <div className="cryptoflip-popup-result-desc">
              {flipResult.won 
                ? `${flipResult.playerChoice === 0 ? 'HEADS' : 'TAILS'} wins!`
                : flipResult.result === 2 
                  ? 'House wins!'
                  : `${flipResult.result === 0 ? 'HEADS' : 'TAILS'} wins!`
              }
            </div>
            {flipResult.won && gameState.streak > 1 && (
              <div className="cryptoflip-popup-streak-display">
                🔥 {gameState.streak}X BATTLE STREAK! 🔥
              </div>
            )}
          </div>
        </div>
      )}

      {/* Flipping indicator */}
      {animationState === 'flipping' && (
        <div className="cryptoflip-flipping-indicator">
          <div className="cryptoflip-flipping-text">⚡ COIN FLIPPING...</div>
          <div className="cryptoflip-flipping-subtext">Determining your fate, warrior!</div>
        </div>
      )}
    </div>
  </div>
)}

          {/* Battle Controls */}
          <div className="cryptoflip-controls">
            <div className="cryptoflip-control-panel">
              <label className="cryptoflip-control-label">💰 STAKE</label>
              <div>
                <input
                  className="cryptoflip-stake-input"
                  type="number"
                  value={gameState.betAmount}
                  onChange={(e) => setGameState(prev => ({ ...prev, betAmount: e.target.value }))}
                  disabled={gameState.isPlaying || adminData.isPaused}
                  min={adminData.minBet}
                  max={Math.min(parseFloat(gameState.balance), parseFloat(adminData.maxBet))}
                  step="1"
                  onFocus={() => playGameSound('buttonClick', 0.1)}
                />
                <div className="cryptoflip-preset-buttons">
                  {['1', '10', '100'].map(amount => (
                    <button
                      key={amount}
                      onClick={() => {
                        playGameSound('buttonClick', 0.2);
                        setGameState(prev => ({ ...prev, betAmount: amount }));
                      }}
                      onMouseEnter={() => playGameSound('buttonHover', 0.1)}
                      disabled={gameState.isPlaying || adminData.isPaused}
                      className="cryptoflip-preset-btn"
                    >
                      {amount}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      playGameSound('buttonClick', 0.2);
                      const maxAmount = Math.min(parseFloat(gameState.balance), parseFloat(adminData.maxBet));
                      setGameState(prev => ({ ...prev, betAmount: Math.floor(maxAmount).toString() }));
                    }}
                    onMouseEnter={() => playGameSound('buttonHover', 0.1)}
                    disabled={gameState.isPlaying || adminData.isPaused}
                    className="cryptoflip-preset-btn max-btn"
                  >
                    MAX
                  </button>
                </div>
              </div>
            </div>

            <div className="cryptoflip-control-panel">
              <label className="cryptoflip-control-label">⚔️ CHOOSE SIDE</label>
              <div className="cryptoflip-choice-grid">
                <button
                  className={`cryptoflip-choice-btn ${gameState.selectedSide === 0 ? 'heads-selected' : ''}`}
                  onClick={() => handleWeaponSelect(0)}
                  onMouseEnter={() => playGameSound('buttonHover', 0.15)}
                  disabled={gameState.isPlaying || adminData.isPaused}
                >
                  <div className="cryptoflip-choice-icon">👑</div>
                  <span className="cryptoflip-choice-text">HEADS</span>
                </button>
                <button
                  className={`cryptoflip-choice-btn ${gameState.selectedSide === 1 ? 'tails-selected' : ''}`}
                  onClick={() => handleWeaponSelect(1)}
                  onMouseEnter={() => playGameSound('buttonHover', 0.15)}
                  disabled={gameState.isPlaying || adminData.isPaused}
                >
                  <div className="cryptoflip-choice-icon">⭐</div>
                  <span className="cryptoflip-choice-text">TAILS</span>
                </button>
              </div>
            </div>

            <div className="cryptoflip-control-panel">
              {adminData.isPaused ? (
                <button
                  className="cryptoflip-action-btn"
                  disabled={true}
                  style={{
                    background: 'rgba(239, 68, 68, 0.3)',
                    color: '#ef4444',
                    borderColor: '#ef4444',
                    cursor: 'not-allowed'
                  }}
                >
                  ⚠️ GAME PAUSED
                </button>
              ) : needsApproval() ? (
                <button
                  className="cryptoflip-action-btn cryptoflip-approve-btn"
                  onClick={handleApprove}
                  onMouseEnter={() => playGameSound('buttonHover', 0.2)}
                  disabled={gameState.isApproving}
                >
                  {gameState.isApproving ? '⏳ APPROVING...' : '🔓 APPROVE BLB'}
                </button>
              ) : (
                <button
                  className="cryptoflip-action-btn cryptoflip-flip-btn"
                  onClick={handleFlip}
                  onMouseEnter={() => playGameSound('buttonHover', 0.2)}
                  disabled={!canPlay()}
                >
                  {gameState.isPlaying ? '🌪️ FLIPPING...' : '🚀 FLIP COIN'}
                </button>
              )}
            </div>

            {gameState.selectedSide !== null && !gameState.isPlaying && !adminData.isPaused && (
              <div className="cryptoflip-reward-display">
                💎 WIN: {formatNumber((parseFloat(gameState.betAmount) * parseFloat(gameStats.effectiveMultiplier)).toString())} BLB
              </div>
            )}
          </div>
        </div>

        {/* Stats Sidebar */}
        <div className="cryptoflip-stats-sidebar">
          <div className="cryptoflip-stats-panel">
            <h3 className="cryptoflip-stats-title">🏅 STATUS</h3>
            <div className="cryptoflip-stats-item">
              <span className="cryptoflip-stats-label">Level</span>
              <span className="cryptoflip-stats-value">{gameState.playerLevel}</span>
            </div>
            <div className="cryptoflip-stats-item">
              <span className="cryptoflip-stats-label">XP</span>
              <span className="cryptoflip-stats-value">{formatNumber(gameState.experience)}</span>
            </div>
            <div className="cryptoflip-stats-item">
              <span className="cryptoflip-stats-label">Streak</span>
              <span className="cryptoflip-stats-value">{gameState.streak}x 🔥</span>
            </div>
            <div className="cryptoflip-stats-item">
              <span className="cryptoflip-stats-label">Rank</span>
              <span className="cryptoflip-stats-value">Warrior</span>
            </div>
          </div>

          {isAdmin && (
            <div className="cryptoflip-stats-panel">
              <h3 className="cryptoflip-stats-title">🛡️ ADMIN INFO</h3>
              <div className="cryptoflip-stats-item">
                <span className="cryptoflip-stats-label">House Balance</span>
                <span className="cryptoflip-stats-value">{formatNumber(adminData.contractBalance)} BLB</span>
              </div>
              <div className="cryptoflip-stats-item">
                <span className="cryptoflip-stats-label">Status</span>
                <span className="cryptoflip-stats-value" style={{ color: adminData.isPaused ? '#ef4444' : '#10b981' }}>
                  {adminData.isPaused ? 'PAUSED' : 'ACTIVE'}
                </span>
              </div>
              <div className="cryptoflip-stats-item">
                <span className="cryptoflip-stats-label">Min Bet</span>
                <span className="cryptoflip-stats-value">{formatNumber(adminData.minBet)} BLB</span>
              </div>
              <div className="cryptoflip-stats-item">
                <span className="cryptoflip-stats-label">Max Bet</span>
                <span className="cryptoflip-stats-value">{formatNumber(adminData.maxBet)} BLB</span>
              </div>
              <div className="cryptoflip-stats-item">
                <span className="cryptoflip-stats-label">House Edge</span>
                <span className="cryptoflip-stats-value" style={{ color: '#ffd700' }}>
                  {(adminData.gameConfig.noWinChance / 100).toFixed(1)}%
                </span>
              </div>
            </div>
          )}

          <div className="cryptoflip-stats-panel">
            <h3 className="cryptoflip-stats-title">🎯 HOW TO PLAY</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ 
                  background: 'linear-gradient(45deg, #dc2626, #ffd700)', 
                  color: '#000', 
                  width: '2rem', 
                  height: '2rem', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontWeight: '800', 
                  fontSize: '0.9rem', 
                  flexShrink: 0,
                  boxShadow: '0 0 8px rgba(255, 215, 0, 0.4)'
                }}>1</div>
                <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600', fontSize: '0.9rem' }}>
                  Set stake amount
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ 
                  background: 'linear-gradient(45deg, #dc2626, #ffd700)', 
                  color: '#000', 
                  width: '2rem', 
                  height: '2rem', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontWeight: '800', 
                  fontSize: '0.9rem', 
                  flexShrink: 0,
                  boxShadow: '0 0 8px rgba(255, 215, 0, 0.4)'
                }}>2</div>
                <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600', fontSize: '0.9rem' }}>
                  Choose HEADS or TAILS
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ 
                  background: 'linear-gradient(45deg, #dc2626, #ffd700)', 
                  color: '#000', 
                  width: '2rem', 
                  height: '2rem', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontWeight: '800', 
                  fontSize: '0.9rem', 
                  flexShrink: 0,
                  boxShadow: '0 0 8px rgba(255, 215, 0, 0.4)'
                }}>3</div>
                <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600', fontSize: '0.9rem' }}>
                  Click FLIP COIN
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ 
                  background: 'linear-gradient(45deg, #dc2626, #ffd700)', 
                  color: '#000', 
                  width: '2rem', 
                  height: '2rem', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontWeight: '800', 
                  fontSize: '0.9rem', 
                  flexShrink: 0,
                  boxShadow: '0 0 8px rgba(255, 215, 0, 0.4)'
                }}>4</div>
                <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600', fontSize: '0.9rem' }}>
                  Win {gameStats.effectiveMultiplier}x your bet!
                </div>
              </div>
            </div>
          </div>

          

          {gameHistory.length > 0 && (
            <div className="cryptoflip-stats-panel">
              <h3 className="cryptoflip-stats-title">📜 HISTORY</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: '200px', overflowY: 'auto' }}>
                {gameHistory.map((battle, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '0.8rem',
                    background: 'rgba(255, 255, 255, 0.03)',
                    borderRadius: '8px',
                    borderLeft: `3px solid ${battle.won ? '#10b981' : '#ef4444'}`,
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>
                      {battle.playerChoice === 0 ? '👑' : '⭐'}
                    </div>
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem', fontWeight: '600' }}>
                        {formatNumber(battle.betAmount)} BLB
                      </div>
                      <div style={{ 
                        color: battle.won ? '#10b981' : '#ef4444', 
                        fontWeight: '800', 
                        fontSize: '0.9rem',
                        textShadow: `0 0 10px ${battle.won ? 'rgba(16, 185, 129, 0.6)' : 'rgba(239, 68, 68, 0.6)'}`
                      }}>
                        {battle.won ? `+${formatNumber(battle.netPayout)}` : 'LOSS'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CryptoFlip;